/**
 * BookingWidgetEmbed - Clean embeddable page for the Quick Booking Widget
 * 
 * Designed for iframe embedding on GHL funnels and external pages.
 * Renders only the widget with no extra branding or UI.
 * Supports auto-resize via postMessage to parent window.
 * 
 * URL Parameters:
 * - first_name (required): Contact's first name
 * - last_name (required): Contact's last name  
 * - email (required): Contact's email
 * - phone (required): Contact's phone number
 * - contact_id (optional): Existing GHL contact ID
 * - calendar_id (optional): GHL Calendar ID
 * - location_id (optional): GHL Location ID
 * 
 * Parent Window Messages:
 * - WIDGET_HEIGHT: { height: number } - For iframe auto-resize
 * - BOOKING_SUCCESS: { appointmentId, contactId, startTime, displayDate, displayTime }
 * - BOOKING_ERROR: { message: string }
 */

import React, { useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QuickBookingWidget } from '@/components/booking';

export default function BookingWidgetEmbed() {
  const [searchParams] = useSearchParams();
  
  // Set clean white background and prevent body scroll for embed
  // This ensures the widget looks clean and fixed positioning works correctly
  useEffect(() => {
    const originalBodyBg = document.body.style.background;
    const originalHtmlBg = document.documentElement.style.background;
    const originalBodyClass = document.body.className;
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    
    // Set white background and remove any dark mode classes
    document.body.style.background = '#FFFFFF';
    document.body.style.backgroundColor = '#FFFFFF';
    document.documentElement.style.background = '#FFFFFF';
    document.documentElement.style.backgroundColor = '#FFFFFF';
    document.body.classList.remove('dark');
    
    // Ensure html/body fill viewport for proper fixed positioning
    document.body.style.minHeight = '100vh';
    document.documentElement.style.minHeight = '100vh';
    
    return () => {
      document.body.style.background = originalBodyBg;
      document.documentElement.style.background = originalHtmlBg;
      document.body.className = originalBodyClass;
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);
  
  // Extract contact info from URL params
  const contact = useMemo(() => ({
    first_name: searchParams.get('first_name') || '',
    last_name: searchParams.get('last_name') || '',
    email: searchParams.get('email') || '',
    phone: searchParams.get('phone') || '',
    contact_id: searchParams.get('contact_id') || null,
  }), [searchParams]);
  
  // Configuration from URL params
  const calendarId = searchParams.get('calendar_id') || undefined;
  const locationId = searchParams.get('location_id') || undefined;
  
  // Validate required fields
  const isValid = contact.first_name && contact.last_name && contact.email && contact.phone;
  
  // Callback handlers - post messages to parent window
  const handleBookingSuccess = (result) => {
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'BOOKING_SUCCESS',
        payload: result
      }, '*');
    }
  };
  
  const handleBookingError = (error) => {
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'BOOKING_ERROR',
        payload: { message: error.message }
      }, '*');
    }
  };
  
  // Show error state if required params are missing
  if (!isValid) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="bg-white rounded-2xl shadow-sm p-6 max-w-sm text-center">
          <p className="text-red-500 font-medium text-sm mb-1">Missing Information</p>
          <p className="text-gray-500 text-xs">
            Contact details are required to show available times.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: '#FFFFFF',
        background: '#FFFFFF'
      }}
    >
      <QuickBookingWidget
        contact={contact}
        calendarId={calendarId}
        locationId={locationId}
        onBookingSuccess={handleBookingSuccess}
        onBookingError={handleBookingError}
      />
    </div>
  );
}
